import { useFlags } from 'launchdarkly-react-client-sdk'
import { useEffect, useRef, useId } from 'react'
import { useFlagRequestsStore } from 'state/useFlagRequestsStore'

type FlagValue = boolean | string | number | null

/**
 * Custom hook to manage feature flags with Bugsnag integration.
 *
 * This hook provides access to LaunchDarkly feature flags and tracks their usage.
 * It sends requests to the `useFlagRequestsStore` to manage the state of accessed flags.
 *
 * - When a flag is accessed, it adds or updates the flag in the store.
 * - On component unmount, it clears all accessed flags for the component instance.
 *
 * Bugsnag is notified of feature flag changes via the `useFlagRequestsStore` listener.
 */
export const useFeatureFlags = <T extends Record<string, FlagValue>>() => {
  const flags = useFlags<T>()
  const accessedFlags = useRef<Set<keyof T>>(new Set()) // Track flags accessed by this component
  const instanceId = useId() // Unique ID for this component instance
  const { addRequest, clearRequest } = useFlagRequestsStore()

  const getFlag = (key: keyof T): T[keyof T] => {
    const currentValue = flags[key]

    // Add the flag to the accessed set (but don't update the store yet)
    accessedFlags.current.add(key)

    return currentValue
  }

  useEffect(() => {
    // Add all accessed flags to the Zustand store after rendering
    accessedFlags.current.forEach((key) => {
      const currentValue = flags[key]

      // Only add the flag if its value is `true`
      if (currentValue === true) {
        const isIdUnique =
          !useFlagRequestsStore.getState().requests[key as string]?.[instanceId]

        // Check if the id is already in the store for this flag
        if (isIdUnique) {
          addRequest({
            id: instanceId,
            key: key as string,
            value: currentValue,
          })
        }
      }
    })
  }, [flags, addRequest, instanceId])

  useEffect(() => {
    const currentAccessedFlags = accessedFlags.current
    return () => {
      // Clear all accessed flags on unmount
      currentAccessedFlags.forEach((key) => {
        clearRequest({
          id: instanceId,
          key: key as string,
        })
      })
      currentAccessedFlags.clear() // Clean up the accessed flags
    }
  }, [clearRequest, instanceId])

  return { getFlag }
}
